
import Vue from "vue";

export default Vue.extend({
  name: "NumberInput",

  props: {
    value: Number,
    label: { type: String, required: false },
    error: String,
    disabled: Boolean,
    loading: Boolean,
    digitable: Boolean,
    type: {
      type: String,
      default: "number",
      validator: (value: string) =>
        ["number", "currency", "integer", "weight", "percentage"].includes(
          value
        ),
    },

    maxNumber: Number,
    minFractionDigits: { type: Number, default: 2 },
    maxFractionDigits: { type: Number, default: 8 },
    dense: Boolean,
    suffix: String,
  },

  data: () => ({
    parsedValue: null as number | null,
    innerError: "",
    refreshKey: 0 as number,
  }),

  computed: {
    hasErrors(): boolean {
      return this.error?.length > 0 || this.innerError !== "";
    },

    formattedValue: {
      get: function (): string | null {
        return this.format(this.value);
      },

      set: function (value: string) {
        this.parsedValue = this.parse(value);
        this.$emit("input", this.parsedValue);
      },
    },

    innerSuffix(): string {
      if (this.suffix) {
        return this.suffix;
      }

      if (this.type === "percentage") {
        return "%";
      }

      if (this.type === "weight") {
        return "kg";
      }

      return "";
    },
  },

  methods: {
    keyPress(event: any) {
      const keyCode = event.keyCode ? event.keyCode : event.which;

      if (keyCode === 44 && this.type === "integer") {
        event.preventDefault();
      }

      if (![44, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57].includes(keyCode)) {
        event.preventDefault();
      }
    },

    onBlur() {
      this.$emit("blur");
      this.innerError = "";
      if (
        this.maxNumber &&
        this.parsedValue &&
        this.parsedValue > this.maxNumber
      ) {
        this.innerError = `O valor máximo é ${this.maxNumber}`;
        this.$emit("input", null);
        return;
      }

      if (this.parsedValue !== this.$props.value)
        this.$emit("input", this.parsedValue);

      this.refreshKey++;
    },

    format(value: number | null): string | null {
      if (!value) {
        return null;
      }

      if (isNaN(value)) {
        return null;
      }

      switch (this.type) {
        case "currency":
          return value.toLocaleString("pt-BR", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: "currency",
            currency: "BRL",
          });

        case "integer":
          return value.toLocaleString("pt-BR", {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          });

        case "weight":
          return value.toLocaleString("pt-BR", {
            maximumFractionDigits: 3,
            minimumFractionDigits: 3,
          });

        case "percentage":
          return value.toLocaleString("pt-BR", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
          });

        default:
          return value.toLocaleString("pt-BR", {
            maximumFractionDigits: this.maxFractionDigits,
            minimumFractionDigits: this.minFractionDigits,
          });
      }
    },

    parse(value: string | null): number | null {
      if (!value) {
        return null;
      }

      const numberStr = value.replaceAll(/[^\d,]/g, "").replaceAll(",", ".");

      const result =
        this.type === "integer" ? parseInt(numberStr) : parseFloat(numberStr);

      return isNaN(result) ? null : result;
    },
  },

  watch: {
    value: {
      handler(value: any) {
        this.formattedValue = this.format(value);
      },
      immediate: true,
    },
  },
});
